import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api } from "../api";
import { AttendanceStatusBadge } from "../components/AttendanceStatusBadge";
import { Notice } from "../components/Layout";
import { openClassroomDisplay } from "../kiosk-lock";
import { timeInTimeZone } from "../time";
import type { InstructorBootstrap, InstructorSessionDetail } from "../types";

export function InstructorShell({ children, displayName }: { children: ReactNode; displayName?: string }) {
  return <>
    <header className="instructor-topbar"><div>
      <a href="/instructor" className="brand-link"><img src="/logo.png" alt="Assemble Studio" /><span><strong>My Classes</strong><small>{displayName || "Instructor"}</small></span></a>
    </div></header>
    <main className="instructor-main">{children}</main>
  </>;
}

export function InstructorPage() {
  const [data, setData] = useState<InstructorBootstrap | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setData(await api<InstructorBootstrap>("/api/admin/instructor/bootstrap")); setError(""); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Could not load assigned classes"); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  return <InstructorShell displayName={data?.staff.displayName}>
    {error && <Notice kind="error">{error}</Notice>}
    {!data && !error && <div className="loading-card">Loading assigned classes…</div>}
    {data && <InstructorClassList data={data} />}
  </InstructorShell>;
}

export function InstructorClassList({ data }: { data: InstructorBootstrap }) {
  return <section className="instructor-stack">
    <div className="instructor-heading"><div><span className="eyebrow">Instructor</span><h1>My Classes</h1></div><p>School date: {data.today}<br />{data.timeZone}</p></div>
    {!data.sessions.length && <div className="instructor-empty">No assigned classes today or upcoming.</div>}
    <div className="instructor-class-grid">{data.sessions.map((session) => <article className="instructor-class-card" key={session.session_id}>
      <div className="instructor-class-date"><strong>{session.class_date === data.today ? "Today" : session.class_date}</strong><span>{session.start_time}–{session.end_time}</span></div>
      <h2>{session.title}</h2>
      <div className="instructor-class-meta"><span className={`status-badge status-${session.status}`}>{session.status}</span><span>{session.attendance_count || 0} checked in</span></div>
      <div className="instructor-actions">
        <a className="primary-link" href={`/instructor/session/${encodeURIComponent(session.session_id)}`}>View Check-ins</a>
        <button className="secondary-button" disabled={session.status !== "open"} onClick={() => { void openClassroomDisplay(session.session_id); }}>Classroom Display</button>
      </div>
    </article>)}</div>
  </section>;
}

export function InstructorSessionPage({ sessionId }: { sessionId: string }) {
  const [data, setData] = useState<InstructorSessionDetail | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setData(await api<InstructorSessionDetail>(`/api/admin/instructor/sessions/${encodeURIComponent(sessionId)}`)); setError(""); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Could not load check-ins"); }
  }, [sessionId]);
  useEffect(() => {
    void load();
    const timer = window.setInterval(load, 5_000);
    return () => window.clearInterval(timer);
  }, [load]);
  return <InstructorShell>
    <a className="instructor-back" href="/instructor">← My Classes</a>
    {error && <Notice kind="error">{error}</Notice>}
    {!data && !error && <div className="loading-card">Loading check-ins…</div>}
    {data && <InstructorCheckins data={data} />}
  </InstructorShell>;
}

export function InstructorCheckins({ data }: { data: InstructorSessionDetail }) {
  return <section className="instructor-stack">
    <header className="instructor-session-heading"><div><span className="eyebrow">Check-ins</span><h1>{data.session.title}</h1><p>{data.session.class_date} · {data.session.start_time}–{data.session.end_time}</p></div><strong>{data.attendance.length}</strong></header>
    <div className="instructor-detail-actions"><button className="secondary-button" disabled={data.session.status !== "open"} onClick={() => { void openClassroomDisplay(data.session.session_id); }}>Classroom Display</button></div>
    {!data.attendance.length && <div className="instructor-empty">No check-ins yet.</div>}
    <div className="instructor-checkins">{data.attendance.map((row) => <article key={row.attendance_id}>
      <div><strong>{row.display_name}</strong><time dateTime={row.checked_in_at}>{timeInTimeZone(row.checked_in_at, data.timeZone)}</time></div>
      <AttendanceStatusBadge />
    </article>)}</div>
  </section>;
}
